import { StreamChat } from 'stream-chat'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useUsersMeForChatQuery } from '@/gql/systemApi'
import { getImagePath } from '@/utils'
import { IMAGE_SIZES } from '@/constants'
import { useQuery } from '@apollo/client'
import { SignInQueryResult } from '@/apollo/AuthRoute'
import { IS_LOGGED_IN } from '@/apollo/queries'
import type { ChatConnectionState } from '../types'

const API_KEY = String(import.meta.env.VITE_STREAMCHAT_PUBLIC_KEY)

export interface ChatClientContextV2Value {
  client: StreamChat | null
  connectionState: ChatConnectionState
  connectUser: () => Promise<void>
  disconnectUser: () => void
  currentUserId: string
}

const defaultValue: ChatClientContextV2Value = {
  client: null,
  connectionState: { status: 'disconnected', error: null },
  connectUser: async () => {},
  disconnectUser: () => {},
  currentUserId: '',
}

export const ChatClientContextV2 =
  createContext<ChatClientContextV2Value>(defaultValue)

export const ChatClientProviderV2: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [client, setClient] = useState<StreamChat | null>(null)
  const [connectionState, setConnectionState] = useState<ChatConnectionState>({
    status: 'disconnected',
    error: null,
  })

  const { data: loginData } = useQuery<SignInQueryResult>(IS_LOGGED_IN)
  const isLoggedIn = Boolean(loginData?.isLoggedIn)

  const { refetch: refetchUserMe, data: currentUserData } =
    useUsersMeForChatQuery({ skip: !isLoggedIn })

  const currentUserId = currentUserData?.users_me?.id ?? ''

  const connectUser = useCallback(async () => {
    setConnectionState({ status: 'connecting', error: null })

    try {
      const refetchedUserData = await refetchUserMe()
      const refetchedUser = refetchedUserData?.data?.users_me
      const userProfile = refetchedUser?.attorneys?.[0]?.profiles?.[0]
      if (!refetchedUser) {
        setConnectionState({ status: 'disconnected', error: null })
        return
      }

      const userId = refetchedUser.id
      const fullName =
        `${userProfile?.first_name ?? refetchedUser.first_name} ${
          userProfile?.last_name ?? refetchedUser.last_name
        }`.trim() ?? ''
      const avatarImageId = refetchedUser.avatar?.id ?? ''
      const avatarPath = getImagePath({
        id: avatarImageId,
        size: IMAGE_SIZES.Medium,
      })
      const userToken =
        refetchedUser.attorneys?.[0]?.stream_chat_keys?.[0]?.chat_token

      if (!userId || !userToken) {
        setConnectionState({ status: 'disconnected', error: null })
        return
      }

      const newClient = StreamChat.getInstance(API_KEY, undefined, {
        timeout: 10000,
      })
      await newClient.connectUser(
        { id: userId, name: fullName, image: avatarPath },
        userToken
      )
      setClient(newClient)
      setConnectionState({ status: 'connected', error: null })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ChatV2: Failed to connect user:', error)
      setConnectionState({
        status: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      })
    }
  }, [refetchUserMe])

  const disconnectUser = useCallback(() => {
    client?.disconnectUser()
    setClient(null)
    setConnectionState({ status: 'disconnected', error: null })
  }, [client])

  useEffect(() => {
    if (isLoggedIn) {
      connectUser()
    }
  }, [isLoggedIn, connectUser])

  const value = useMemo(
    () => ({
      client,
      connectionState,
      connectUser,
      disconnectUser,
      currentUserId,
    }),
    [client, connectionState, connectUser, disconnectUser, currentUserId]
  )

  return (
    <ChatClientContextV2.Provider value={value}>
      {children}
    </ChatClientContextV2.Provider>
  )
}
